import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import DataTable from '../../components/ui/data-table';
import EmptyState from '../../components/ui/empty-state.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { userService } from '../../services/userService';
import { Users, Plus, Edit, Trash2, Mail } from 'lucide-react';

const UsersPage = () => {
  const [users, setUsers] = React.useState([]);
  const [stats, setStats] = React.useState({
    total: 0,
    active: 0,
    students: 0,
    staff: 0
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [limit] = React.useState(10);
  const [searchTerm, setSearchTerm] = React.useState('');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        page,
        limit,
        role: 'student',
        ...(searchTerm && { search: searchTerm })
      };
      const res = await userService.getStudents(params);
      const students = res.data?.data?.students || res.data?.data || [];
      setUsers(students);
      
      // Update stats
      const pagination = res.data?.data?.pagination || {};
      setStats({
        total: pagination.total || students.length,
        active: students.filter(s => s.isActive !== false).length,
        students: pagination.total || students.length,
        staff: 0
      });
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load students');
      console.error('Error fetching students:', e);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchUsers();
  }, [page, searchTerm]);

  const columns = [
    { 
      id: 'name', 
      header: 'Name', 
      accessorKey: 'name', 
      sortable: true,
      cell: ({ row }) => {
        const student = row.original;
        if (!student) return 'N/A';
        const firstName = student.firstName || student.profile?.firstName || '';
        const lastName = student.lastName || student.profile?.lastName || '';
        return `${firstName} ${lastName}`.trim() || student.email || 'N/A';
      }
    },
    { id: 'email', header: 'Email', accessorKey: 'email', sortable: true },
    { 
      id: 'admissionNumber', 
      header: 'Admission Number', 
      accessorKey: 'admissionNumber',
      cell: ({ row }) => {
        if (!row?.original) return 'N/A';
        return row.original.profile?.admissionNumber || row.original.admissionNumber || 'N/A';
      }
    },
    { 
      id: 'class', 
      header: 'Class', 
      accessorKey: 'class',
      cell: ({ row }) => {
        if (!row?.original) return 'N/A';
        const enrollment = row.original.enrollments?.[0];
        return enrollment?.class?.name || 'Not enrolled';
      }
    },
    { 
      id: 'status', 
      header: 'Status', 
      accessorKey: 'status', 
      sortable: true,
      cell: ({ row }) => {
        if (!row?.original) return 'N/A';
        return row.original.isActive !== false ? 'Active' : 'Inactive';
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        if (!row?.original) return null;
        return (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline">
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </Button>
            <Button size="sm" variant="outline">
              <Mail className="w-4 h-4 mr-1" />
              Message
            </Button>
            <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        );
      }
    }
  ];

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={fetchUsers}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Student Management</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            Export Users
          </Button>
          <Button size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* User Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Students</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-yellow-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Inactive</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total - stats.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Enrolled</p>
                <p className="text-2xl font-bold text-gray-900">{users.filter(u => u.enrollments?.length > 0).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {(!users || users.length === 0) ? (
        <EmptyState 
          icon={Users}
          title="No students found"
          description={searchTerm ? "No students match your search criteria." : "No students have been added to the system yet."}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <DataTable 
              data={users} 
              columns={columns} 
              searchable 
              onSearch={(value) => {
                setSearchTerm(value);
                setPage(1);
              }}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UsersPage;
